/**
 * PermissionsEditor Component
 *
 * Advanced permissions editor for Head Coach to customize user permissions
 */

import React, { useState, useEffect } from 'react';
import {
  DEFAULT_PERMISSIONS,
  PermissionLevel,
  getPermissionLevelLabel,
  UserPermissions,
} from '../../config/permissions';

interface PermissionsEditorProps {
  roleValue: string;
  customPermissions?: Partial<UserPermissions> | null;
  onChange: (permissions: Partial<UserPermissions> | null) => void;
  disabled?: boolean;
  backendDefaultPermissions?: Record<string, UserPermissions> | null; // Optional: Use backend defaults if available
  currentUserPermissions?: Partial<UserPermissions> | null; // Current user's permissions to limit what can be assigned
}

export const PermissionsEditor: React.FC<PermissionsEditorProps> = ({
  roleValue,
  customPermissions,
  onChange,
  disabled = false,
  backendDefaultPermissions = null,
  currentUserPermissions = null,
}) => {
  const [editedPermissions, setEditedPermissions] = useState<Partial<UserPermissions>>({});

  // Use backend defaults if available, otherwise use frontend fallback
  const defaultPerms =
    backendDefaultPermissions?.[roleValue] || DEFAULT_PERMISSIONS[roleValue] || DEFAULT_PERMISSIONS.statTaker;

  useEffect(() => {
    if (customPermissions) {
      setEditedPermissions(customPermissions);
    } else {
      setEditedPermissions({});
    }
  }, [customPermissions]);

  const handlePermissionChange = (path: string, value: PermissionLevel | boolean) => {
    const pathParts = path.split('.');
    const newPerms = { ...editedPermissions };

    // Navigate to the nested property and set it
    let current: any = newPerms;
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }
    current[pathParts[pathParts.length - 1]] = value;

    setEditedPermissions(newPerms);
    onChange(newPerms);
  };

  const getPermissionValue = (path: string): PermissionLevel | boolean | undefined => {
    // Get from edited permissions
    const pathParts = path.split('.');
    let current: any = editedPermissions;
    for (const part of pathParts) {
      current = current?.[part];
      if (current === undefined) {
        // Fall back to default if not customized
        let defaultCurrent: any = defaultPerms;
        for (const part of pathParts) {
          defaultCurrent = defaultCurrent[part];
          if (defaultCurrent === undefined) return undefined;
        }
        return defaultCurrent;
      }
    }
    return current;
  };

  // Check if current user can assign a specific permission level
  const canAssignPermission = (path: string, level: PermissionLevel): boolean => {
    // If no currentUserPermissions provided, allow everything (Head Coach, Employee)
    if (!currentUserPermissions) return true;

    // Navigate to the permission in current user's permissions
    const pathParts = path.split('.');
    let current: any = currentUserPermissions;
    for (const part of pathParts) {
      current = current?.[part];
      if (current === undefined) {
        // If user doesn't have this permission defined, default to not allowing assignment
        return false;
      }
    }

    // If current is a boolean (like dataSharing), just check if it's true
    if (typeof current === 'boolean') {
      return current;
    }

    // Check permission level hierarchy
    const permissionHierarchy: Record<string, number> = {
      [PermissionLevel.NONE]: 0,
      [PermissionLevel.VIEW]: 1,
      [PermissionLevel.EDIT]: 2,
      [PermissionLevel.AED]: 3,
    };

    const userLevel = permissionHierarchy[current as PermissionLevel] || 0;
    const requestedLevel = permissionHierarchy[level] || 0;

    // User can only assign permission levels they have or lower
    return userLevel >= requestedLevel;
  };

  return (
    <div className="space-y-4">
      {/* Permissions Grid */}
      <div className="space-y-6">
        {/* Games Section */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Games</h3>
          </div>
          <div className="p-4 space-y-4">
            <PermissionSelect
              label="Upload Schedule"
              value={getPermissionValue('games.uploadSchedule') as PermissionLevel}
              onChange={(val) => handlePermissionChange('games.uploadSchedule', val)}
              disabled={disabled}
              help="Allow user to upload game schedules in bulk"
              canAssignLevel={(level) => canAssignPermission('games.uploadSchedule', level)}
            />
            <PermissionSelect
              label="Manage Game"
              value={getPermissionValue('games.game') as PermissionLevel}
              onChange={(val) => handlePermissionChange('games.game', val)}
              disabled={disabled}
              help="Allow user to create, edit, or delete individual games"
              canAssignLevel={(level) => canAssignPermission('games.game', level)}
            />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">Practice / Intra Squad Games</label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Allow this user to see Practice mode and enter Intra Squad game stats. Off by default for Stat
                  Takers and Stat Managers.
                </p>
              </div>
              <select
                value={getPermissionValue('games.practiceGames') ? 'yes' : 'no'}
                onChange={(e) => handlePermissionChange('games.practiceGames', e.target.value === 'yes')}
                disabled={
                  disabled ||
                  (currentUserPermissions !== null && currentUserPermissions.games?.practiceGames === false)
                }
                className="sm:ml-4 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[160px]"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Game Menu Subsection */}
            <div className="ml-4 pl-4 border-l-2 border-gray-200 space-y-3">
              <h4 className="font-medium text-gray-700 text-sm">Game Menu</h4>
              <PermissionSelect
                label="Stats"
                value={getPermissionValue('games.gameMenu.stats') as PermissionLevel}
                onChange={(val) => handlePermissionChange('games.gameMenu.stats', val)}
                disabled={disabled}
                help="Allow user to add, edit, or delete game statistics"
                canAssignLevel={(level) => canAssignPermission('games.gameMenu.stats', level)}
              />
              <YesNoToggle
                label="Allow user to edit stats after lock"
                value={getPermissionValue('games.gameMenu.statsLockAfterTimeout') as PermissionLevel}
                onChange={(val) => handlePermissionChange('games.gameMenu.statsLockAfterTimeout', val)}
                disabled={
                  disabled || !canAssignPermission('games.gameMenu.statsLockAfterTimeout', PermissionLevel.EDIT)
                }
                help="Games are locked 24 hours after start time. Enable to let user bypass this lock."
              />
              <PermissionSelect
                label="Details"
                value={getPermissionValue('games.gameMenu.details') as PermissionLevel}
                onChange={(val) => handlePermissionChange('games.gameMenu.details', val)}
                disabled={disabled}
                help="Allow user to view or edit game details (date, time, location, etc.)"
                allowedLevels={[PermissionLevel.EDIT, PermissionLevel.VIEW, PermissionLevel.NONE]}
                canAssignLevel={(level) => canAssignPermission('games.gameMenu.details', level)}
              />
              <PermissionSelect
                label="KPIs"
                value={getPermissionValue('games.gameMenu.kpis') as PermissionLevel}
                onChange={(val) => handlePermissionChange('games.gameMenu.kpis', val)}
                disabled={disabled}
                help="Allow user to edit Key Performance Indicators (goals, benchmarks)"
                allowedLevels={[PermissionLevel.EDIT, PermissionLevel.VIEW, PermissionLevel.NONE]}
                canAssignLevel={(level) => canAssignPermission('games.gameMenu.kpis', level)}
              />
            </div>
          </div>
        </div>

        {/* Top Level Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <PermissionSelect
              label="Seasons"
              value={getPermissionValue('seasons') as PermissionLevel}
              onChange={(val) => handlePermissionChange('seasons', val)}
              disabled={disabled}
              canAssignLevel={(level) => canAssignPermission('seasons', level)}
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <PermissionSelect
              label="Users"
              value={getPermissionValue('users') as PermissionLevel}
              onChange={(val) => handlePermissionChange('users', val)}
              disabled={disabled}
              canAssignLevel={(level) => canAssignPermission('users', level)}
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <PermissionSelect
              label="Players"
              value={getPermissionValue('players') as PermissionLevel}
              onChange={(val) => handlePermissionChange('players', val)}
              disabled={disabled}
              canAssignLevel={(level) => canAssignPermission('players', level)}
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <PermissionSelect
              label="Opponents"
              value={getPermissionValue('opponents') as PermissionLevel}
              onChange={(val) => handlePermissionChange('opponents', val)}
              disabled={disabled}
              canAssignLevel={(level) => canAssignPermission('opponents', level)}
            />
          </div>
        </div>

        {/* Data Sharing Toggle */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Data Sharing</label>
              <p className="text-sm text-gray-600 mt-1">Allow user to access data sharing features</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={getPermissionValue('dataSharing') as boolean}
                onChange={(e) => handlePermissionChange('dataSharing', e.target.checked)}
                disabled={disabled || (currentUserPermissions !== null && !currentUserPermissions.dataSharing)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual Permission Select Component
 */
interface PermissionSelectProps {
  label: string;
  value: PermissionLevel;
  onChange: (value: PermissionLevel) => void;
  disabled?: boolean;
  help?: string;
  allowedLevels?: PermissionLevel[]; // Optional: restrict which levels are available
  canAssignLevel?: (level: PermissionLevel) => boolean; // Optional: check if user can assign this level
}

const PermissionSelect: React.FC<PermissionSelectProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  help,
  allowedLevels,
  canAssignLevel,
}) => {
  // Default to all levels if not specified
  const allLevels = allowedLevels || [
    PermissionLevel.AED,
    PermissionLevel.EDIT,
    PermissionLevel.VIEW,
    PermissionLevel.NONE,
  ];

  // Filter levels based on what the user can assign
  const availableLevels = canAssignLevel ? allLevels.filter((level) => canAssignLevel(level)) : allLevels;

  // If no levels are available, only show NONE
  const levels = availableLevels.length > 0 ? availableLevels : [PermissionLevel.NONE];

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as PermissionLevel)}
        disabled={disabled}
        className="sm:ml-4 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[160px]"
      >
        {levels.map((level) => (
          <option key={level} value={level}>
            {getPermissionLevelLabel(level)}
          </option>
        ))}
      </select>
    </div>
  );
};

/**
 * Yes/No Toggle Component for Stats Lock After Timeout
 */
interface YesNoToggleProps {
  label: string;
  value: PermissionLevel;
  onChange: (value: PermissionLevel) => void;
  disabled?: boolean;
  help?: string;
}

const YesNoToggle: React.FC<YesNoToggleProps> = ({ label, value, onChange, disabled = false, help }) => {
  // Convert permission level to Yes/No
  // EDIT = Yes (can bypass lock), NONE = No (cannot bypass lock)
  const isYes = value === PermissionLevel.EDIT || value === PermissionLevel.AED;

  const handleChange = (newValue: string) => {
    onChange(newValue === 'yes' ? PermissionLevel.EDIT : PermissionLevel.NONE);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
      </div>
      <select
        value={isYes ? 'yes' : 'no'}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        className="sm:ml-4 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[160px]"
      >
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
};

export default PermissionsEditor;
